import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import { useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { useAdminContext } from '../session'

interface AudienceTop {
  key: string
  visitors: number
  page_views: number
}

interface Audience {
  days: number
  visitors_today: number
  visitors_period: number
  page_views_period: number
  visitors_all_time: number
  page_views_all_time: number
  daily: { day: string; visitors: number; page_views: number }[]
  top_pages: AudienceTop[]
  top_referrers: AudienceTop[]
  devices: AudienceTop[]
  /** Le « en ce moment » de la bulle de l'accueil ; `null` si Redis ne répond pas. */
  online_now: number | null
  online_window_seconds: number
}

const PERIODS = [7, 30, 90, 365] as const

/**
 * Combien de personnes ont visité le site.
 *
 * Mesuré sans cookie ni identifiant chez le visiteur : un visiteur est compté
 * une fois par jour. Le total additionne ces journées — quelqu'un venu trois
 * jours compte trois fois. L'écran le dit, plutôt que de laisser croire à un
 * nombre de personnes distinctes qu'aucune mesure respectueuse ne peut donner.
 */
export function AdminAudiencePage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30)

  useEffect(() => {
    document.title = t('admin.section.audience')
  }, [t])

  // Relu toutes les 30 s : « en ce moment » n'a de sens que s'il bouge.
  const query = useAdminQuery<Audience>(['admin-audience', days], `/admin/audience?days=${days}`, eventId, {
    refetchInterval: 30_000,
  })
  const data = query.data
  const number = new Intl.NumberFormat(i18n.language)

  return (
    <>
      <AdminHeader
        title={t('admin.section.audience')}
        description={t('admin.audience.description')}
        action={
          <div className="flex gap-1 rounded-full border border-line p-1" role="group">
            {PERIODS.map((period) => (
              <button
                key={period}
                type="button"
                aria-pressed={period === days}
                onClick={() => setDays(period)}
                className={cn(
                  'min-h-10 rounded-full px-3 py-1 text-xs transition lg:min-h-0',
                  period === days
                    ? 'bg-bg-overlay text-content-primary'
                    : 'text-content-muted hover:text-content-secondary',
                )}
              >
                {t('admin.audience.days', { count: period })}
              </button>
            ))}
          </div>
        }
      />

      {query.isPending ? <Skeleton className="h-72 w-full" /> : null}
      {query.isError ? <p className="text-sm text-state-danger">{query.error.message}</p> : null}

      {data ? (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
            <Tile
              label={t('admin.audience.now')}
              value={data.online_now !== null ? number.format(data.online_now) : '—'}
              sub={
                data.online_now !== null
                  ? t('admin.audience.nowSub', { count: Math.round(data.online_window_seconds / 60) })
                  : t('admin.audience.nowUnknown')
              }
              live={data.online_now !== null}
            />
            <Tile label={t('admin.audience.today')} value={number.format(data.visitors_today)} />
            <Tile
              label={t('admin.audience.period', { count: data.days })}
              value={number.format(data.visitors_period)}
              sub={t('admin.audience.views', { count: data.page_views_period })}
            />
            <Tile
              label={t('admin.audience.allTime')}
              value={number.format(data.visitors_all_time)}
              sub={t('admin.audience.views', { count: data.page_views_all_time })}
            />
            <Tile
              label={t('admin.audience.mobile')}
              value={`${share(data.devices, 'mobile')} %`}
            />
          </div>

          <Card>
            <h2 className="text-sm font-medium text-content-primary">{t('admin.audience.perDay')}</h2>
            <DailyBars daily={data.daily} locale={i18n.language} />
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <TopList title={t('admin.audience.topPages')} rows={data.top_pages} empty={t('admin.audience.empty')} />
            <TopList
              title={t('admin.audience.topReferrers')}
              rows={data.top_referrers}
              empty={t('admin.audience.direct')}
            />
          </div>

          <p className="max-w-3xl text-xs text-content-muted">{t('admin.audience.method')}</p>
        </div>
      ) : null}
    </>
  )
}

function share(devices: AudienceTop[], key: string): number {
  const total = devices.reduce((sum, row) => sum + row.page_views, 0)
  const part = devices.find((row) => row.key === key)?.page_views ?? 0
  return total ? Math.round((part / total) * 100) : 0
}

function Tile({
  label,
  value,
  sub,
  live = false,
}: {
  label: string
  value: string
  sub?: string
  /** Un point de braise : le chiffre est en direct, pas un cumul. */
  live?: boolean
}) {
  return (
    <Card>
      <p className="flex items-center gap-2 text-caption uppercase tracking-wide text-content-muted">
        {live ? <span aria-hidden className="h-2 w-2 rounded-full bg-ember-400 shadow-ember" /> : null}
        {label}
      </p>
      <p className="mt-2 font-display text-display-sm tabular-nums text-content-primary">{value}</p>
      {sub ? <p className="mt-1 text-xs text-content-muted">{sub}</p> : null}
    </Card>
  )
}

/**
 * Une barre par jour, une seule teinte : c'est une grandeur, pas une identité.
 * Le survol (ou le focus clavier) donne la valeur exacte du jour.
 */
function DailyBars({
  daily,
  locale,
}: {
  daily: Audience['daily']
  locale: string
}) {
  const { t } = useTranslation()
  const [active, setActive] = useState<number | null>(null)
  const max = Math.max(1, ...daily.map((entry) => entry.visitors))
  const format = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' })
  const shown = active !== null ? daily[active] : daily[daily.length - 1]

  return (
    <div className="mt-4">
      <p className="h-5 text-sm text-content-secondary" aria-live="polite">
        {shown
          ? t('admin.audience.dayDetail', {
              date: format.format(new Date(shown.day)),
              visitors: shown.visitors,
              views: shown.page_views,
            })
          : null}
      </p>
      <div
        className="mt-3 flex h-48 items-end gap-[2px] border-b border-line-subtle"
        onMouseLeave={() => setActive(null)}
      >
        {daily.map((entry, index) => (
          <button
            key={entry.day}
            type="button"
            onMouseEnter={() => setActive(index)}
            onFocus={() => setActive(index)}
            aria-label={`${format.format(new Date(entry.day))} : ${entry.visitors}`}
            className="group flex h-full min-w-0 flex-1 items-end"
          >
            <span
              className={cn(
                'block w-full rounded-t-[4px] transition-[height,background-color] duration-500',
                index === active ? 'bg-champagne-300' : 'bg-champagne-400/60 group-hover:bg-champagne-300',
              )}
              style={{ height: `${entry.visitors ? Math.max(2, (entry.visitors / max) * 100) : 0}%` }}
            />
          </button>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-caption text-content-muted">
        <span>{daily[0] ? format.format(new Date(daily[0].day)) : ''}</span>
        <span>{t('admin.audience.max', { count: max })}</span>
        <span>{daily.at(-1) ? format.format(new Date(daily.at(-1)!.day)) : ''}</span>
      </div>
    </div>
  )
}

function TopList({ title, rows, empty }: { title: string; rows: AudienceTop[]; empty: string }) {
  const { t } = useTranslation()
  const max = Math.max(1, ...rows.map((row) => row.visitors))
  return (
    <Card>
      <h2 className="text-sm font-medium text-content-primary">{title}</h2>
      {rows.length === 0 ? <p className="mt-3 text-sm text-content-muted">{empty}</p> : null}
      <ul className="mt-3 space-y-2">
        {rows.map((row) => (
          <li key={row.key}>
            <div className="flex justify-between gap-3 text-sm">
              <span className="min-w-0 truncate text-content-secondary">{row.key}</span>
              <span className="shrink-0 tabular-nums text-content-primary">
                {t('admin.audience.visitorsShort', { count: row.visitors })}
              </span>
            </div>
            <div className="mt-1 h-1 rounded-full bg-bg-overlay">
              <div
                className="h-full rounded-full bg-champagne-400/60"
                style={{ width: `${(row.visitors / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  )
}
